/*
 * This file is part of the Convergence API Server.
 *
 * (c) Convergence <https://convergence.finance/>
 */

import React from "react";
import { BRAND_COLOURS } from "../../../constants";
import { b64EncodeUnicode } from "../../../utils/CommonUtils";
import { Card, CardBody } from "../../theme/3.0/Card";
import Col from "../../theme/3.0/Grid/Col";
import Row from "../../theme/3.0/Grid/Row";
import Link from "../../theme/3.0/Link";

export const getResourceParam = (resourceTag) => {
    let serializedFilters = resourceTag
        ? b64EncodeUnicode(JSON.stringify({ tags: [resourceTag] }))
        : null;

    let queryString = [];

    if (serializedFilters) {
        queryString.push(encodeURIComponent(serializedFilters));
    }
    return queryString;
};

const InitiativeCards = ({ specialInitiative }) => {
    return (
        <React.Fragment>
            <style jsx>
                {`
                    .title {
                        display: inline-block;
                        margin: 0 0 1rem 0;
                        padding: 10px;
                        background-color: rgba(0, 0, 0, 0.5);
                        -webkit-font-smoothing: antialiased;
                        color: ${BRAND_COLOURS.white};
                    }
                    .number {
                        font-size: 5em;
                        text-align: center;
                        color: #14588c;
                    }
                    .chart-title {
                        text-align: center;
                        text-transform: uppercase;
                        margin-bottom: 20px;
                    }
                    .chart-wrapper {
                        background-color: #f8f9fa;
                        border: 0.5px solid #e9ecef;
                        padding: 20px 0;
                        min-height: 265px;
                    }
                    .how-it-works {
                        text-align: center;
                    }
                    .step-title {
                        color: #4a4a4a;
                    }

                    .window-box {
                        margin-bottom: 1rem;
                    }

                    .window-box:last-of-type {
                        margin-bottom: 0;
                    }

                    .thumbnail {
                        padding: 0.25rem;
                        border-radius: 0;
                        max-width: 100%;
                        height: auto;
                    }

                    .window-links {
                        margin-top: 1rem;
                    }

                    .window-description > :global(p:last-of-type) {
                        margin-bottom: 0;
                    }
                `}
            </style>
            <div className={"window-box"} key={specialInitiative.id}>
                <Card background={"white"}>
                    <CardBody>
                        <Row>
                            <Col lg={10}>
                                <h6 className="font-weight-bold text-uppercase">
                                    {specialInitiative.initiativeName}
                                </h6>
                                <div className={"window-description"}>
                                    {specialInitiative.shortDescription}
                                </div>
                            </Col>
                            <Col lg={2} alignSelf={"center"} justify={"center"}>
                                {specialInitiative.image && (
                                    <img
                                        src={`${specialInitiative.image}?h=80&q=100&fit=scale`}
                                        alt={specialInitiative.initiativeName}
                                        title={specialInitiative.initiativeName}
                                        className={"thumbnail"}
                                    />
                                )}

                                <div className={"window-links"}>
                                    {specialInitiative.initiativeFeatures.indexOf(
                                        "Resources"
                                    ) >= 0 ? (
                                        <Link
                                            routeKey={"knowledge"}
                                            query={{
                                                filters: getResourceParam(
                                                    specialInitiative.tags
                                                )
                                            }}
                                            label={"See Resources"}
                                            size={"block"}
                                            colour={"success"}
                                            isButton
                                        />
                                    ) : null}
                                    {/* {specialInitiative.initiativeFeatures.indexOf(
                                        "Deals"
                                    ) >= 0 ? (
                                        <Link
                                            routeKey={
                                                "special-initiatives-deals"
                                            }
                                            params={{
                                                pageSlug:
                                                    specialInitiative.pageSlug
                                            }}
                                            label={"View Transactions"}
                                            size={"block"}
                                            isButton
                                        />
                                    ) : null} */}

                                    {specialInitiative.initiativeFeatures.indexOf(
                                        "Special Mode"
                                    ) >= 0 ? (
                                        <Link
                                            routeKey={
                                                specialInitiative.learnMoreLink ===
                                                undefined
                                                    ? "special-initiatives-very-special-initiative"
                                                    : specialInitiative.learnMoreLink
                                            }
                                            params={
                                                specialInitiative.learnMoreLink ===
                                                undefined
                                                    ? {
                                                          pageSlug:
                                                              specialInitiative.pageSlug
                                                      }
                                                    : undefined
                                            }
                                            label={"Learn More"}
                                            size={"block"}
                                            colour={"border-info"}
                                            isButton
                                        />
                                    ) : null}

                                    {specialInitiative.initiativeFeatures.indexOf(
                                        "Page"
                                    ) >= 0 ? (
                                        <Link
                                            routeKey={
                                                specialInitiative.learnMoreLink ===
                                                undefined
                                                    ? "special-initiatives-initiative"
                                                    : "news-and-events-view-news"
                                            }
                                            params={
                                                specialInitiative.learnMoreLink ===
                                                undefined
                                                    ? {
                                                          pageSlug:
                                                              specialInitiative.pageSlug
                                                      }
                                                    : {
                                                          id:
                                                              specialInitiative.learnMoreLink
                                                      }
                                            }
                                            label={"Learn More"}
                                            size={"block"}
                                            colour={"border-info"}
                                            isButton
                                        />
                                    ) : null}
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </div>
        </React.Fragment>
    );
};

const SpecialInitiativesOverview = ({ specialInitiatives }) => {
    return (
        <React.Fragment>
            <style jsx global>
                {`
                    .title {
                        display: inline-block;
                        margin: 0 0 1rem 0;
                        padding: 10px;
                        background-color: rgba(0, 0, 0, 0.5);
                        -webkit-font-smoothing: antialiased;
                        color: ${BRAND_COLOURS.white};
                        text-transform: initial;
                    }
                    .title::first-line {
                        text-transform: initial;
                    }
                    .number {
                        font-size: 5em;
                        text-align: center;
                        color: #14588c;
                    }
                    .chart-title {
                        text-align: center;
                        text-transform: uppercase;
                        margin-bottom: 20px;
                    }
                    .chart-wrapper {
                        background-color: #f8f9fa;
                        border: 0.5px solid #e9ecef;
                        padding: 20px 0;
                        min-height: 265px;
                    }
                    .how-it-works {
                        text-align: center;
                    }
                    .step-title {
                        color: #4a4a4a;
                    }

                    .window-box {
                        margin-bottom: 1rem;
                    }

                    .window-box:last-of-type {
                        margin-bottom: 0;
                    }

                    .thumbnail {
                        padding: 0.25rem;
                        border-radius: 0;
                        max-width: 100%;
                        height: auto;
                    }

                    .window-links {
                        margin-top: 1rem;
                    }

                    .window-description > :global(p:last-of-type) {
                        margin-bottom: 0;
                    }
                    #content > section:first-child > div:first-child {
                        margin-top: 0 !important;
                        padding-top: 117px;
                    }
                `}
            </style>
            <section>
                <div
                    className={"d-flex align-items-center"}
                    style={{
                        backgroundImage:
                            "url(/static/cover-images/fcdo-header.webp)",
                        backgroundSize: "cover",
                        height: "477px",
                        width: "100%",
                        backgroundPositionY: "center"
                    }}
                >
                    <div className={"container"}>
                        <h2 className="title font-weight-bold">
                            Special Initiatives
                        </h2>
                        <h3 className={"title h5 mb-0 w-100"}>
                            In partnership with select donors and funders,
                            Convergence hosts various special initiatives to
                            advance the field of blended finance.
                        </h3>
                    </div>
                </div>
            </section>

            <section className="fluid-background">
                <div className="container pt-4 pb-4">
                    <h5 className="text-uppercase mb-4">
                        Working Groups and Initiatives
                    </h5>
                    <h3 className={"h5 mb-4 w-100"}>
                        Convergence deploys its blended finance expertise and
                        network to convene organizations in workshops and
                        working groups that pursue specific
                        objectives/improvements for private investment
                        mobilization and blended finance. Learn more about the
                        blended finance special initiatives being led and
                        co-convened by Convergence to increase the quantity and
                        quality of investment in developing countries.
                    </h3>
                    <h4 className="text-uppercase mb-4">Current</h4>

                    {specialInitiatives &&
                        specialInitiatives.length > 0 &&
                        specialInitiatives
                            .filter(
                                (initiative) => initiative.status === "Current"
                            )
                            .map((specialInitiative) => (
                                <InitiativeCards
                                    specialInitiative={specialInitiative}
                                />
                            ))}
                    <h4 className="text-uppercase mt-4 mb-4">Past</h4>

                    {specialInitiatives &&
                        specialInitiatives.length > 0 &&
                        specialInitiatives
                            .filter(
                                (initiative) => initiative.status === "Past"
                            )
                            .map((specialInitiative) => (
                                <InitiativeCards
                                    specialInitiative={specialInitiative}
                                />
                            ))}
                </div>
            </section>
        </React.Fragment>
    );
};

export default SpecialInitiativesOverview;
